import { useEffect, useMemo } from 'react';

import { GX_API_URL } from '@/constants/env';
import { useDataHubUserConfig } from '@/features/DataHub/hooks/useDataHubUserConfig';
import { useSelectedSymbols } from '@/features/DataHub/hooks/useSelectedSymbols';
import { useExportMeta } from '@/features/DataHub/modules/Export/context/ExportMetaContext';
import { useExportPreview } from '@/features/DataHub/modules/Export/context/ExportPreviewContext';
import { useExportRequest } from '@/features/DataHub/modules/Export/context/ExportRequestContext';
import { useExportSettings } from '@/features/DataHub/modules/Export/context/ExportSettingsContext';
import { getPreviewContent } from '@/features/DataHub/modules/Export/services';
import type { ExportOption, PreviewConfig } from '@/features/DataHub/modules/Export/types';
import { buildPreviewContext } from '@/features/DataHub/modules/Export/utils';

import useActivePreview from './useActivePreview';
import { DEFAULT_API_KEY_PLACEHOLDER } from './useExportApiKey';

const usePreviewData = (exportOption: ExportOption, previewConfig: PreviewConfig | undefined) => {
  const { exportSettings } = useExportSettings();
  const { exportApiRequestString } = useExportMeta();
  const { setPreviewLoading } = useExportPreview();
  const { capturePosthogEvent } = useExportRequest();

  const symbols = useSelectedSymbols();
  const { timezoneValue } = useDataHubUserConfig();

  // Use useActivePreview which only calls the hook for the active export type
  const { loading, error, fileContent, downloadFile } = useActivePreview({
    exportOption,
    previewConfig,
    capturePosthogEvent,
    exportApiRequestString,
    exportSettings
  });

  useEffect(() => {
    setPreviewLoading(loading);
    return () => {
      setPreviewLoading(false);
    };
  }, [loading, setPreviewLoading]);

  const previewContext = useMemo(
    () =>
      buildPreviewContext({
        exportSettings,
        symbols,
        timezone: timezoneValue,
        apiKey: DEFAULT_API_KEY_PLACEHOLDER,
        gxURL: GX_API_URL
      }),
    [exportSettings, symbols, timezoneValue]
  );

  const content = useMemo(
    () =>
      getPreviewContent({
        previewConfig,
        exportOption,
        fileContent,
        exportApiRequestString,
        context: previewContext
      }),
    [previewConfig, exportOption, fileContent, exportApiRequestString, previewContext]
  );

  return { loading, error, fileContent, downloadFile, content };
};

export default usePreviewData;
